import React from 'react';
import classNames from 'classnames';
import { prefixClass } from 'lib/utils';
import './GroupHeader.scss';
import { IconChevron, IconChevronDown } from 'components/Icons';

export interface GroupHeaderProps {
  /** Group label (e.g., "Today", "2024") */
  label: string;
  /** Number of items in the group */
  count: number;
  /** Whether the group is expanded */
  isExpanded: boolean;
  /** Whether this group header has keyboard focus */
  isFocused?: boolean;
  /** Unique key for the group (used for data attribute) */
  groupKey: string;
  /** Toggle expand/collapse */
  onToggle: () => void;
  /** Double-click to expand/collapse all groups */
  onDoubleClick?: () => void;
}

/**
 * GroupHeader - Collapsible header for date groups in the browser list.
 */
export const GroupHeader = ({
  label,
  count,
  isExpanded,
  isFocused = false,
  groupKey,
  onToggle,
  onDoubleClick,
}: GroupHeaderProps) => {
  const prefix = prefixClass('ksq2-group-header');

  const renderLabel = (): React.ReactNode => {
    if (groupKey.startsWith('month-')) {
      const [, yearMonth] = groupKey.split('month-');
      const [year, month] = yearMonth.split('-').map(Number);
      const date = new Date(year, month - 1, 1);
      const monthName = date.toLocaleDateString('en-US', { month: 'long' });

      return (
        <>
          <span className={prefix('label-month')}>{monthName}</span>{' '}
          <span className={prefix('label-year')}>{year}</span>
        </>
      );
    }

    return label;
  };

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onToggle();
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onDoubleClick?.();
  };

  return (
    <div
      className={classNames(prefix(), {
        [prefix('collapsed')]: !isExpanded,
        [prefix('focused')]: isFocused,
        [prefix('blockers')]: groupKey === 'blockers',
      })}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      data-group-key={groupKey}
      role="treeitem"
      aria-expanded={isExpanded}
      tabIndex={isFocused ? 0 : -1}
    >
      <span className={prefix('chevron')}>{isExpanded ? <IconChevronDown /> : <IconChevron />}</span>
      <span className={prefix('label')}>{renderLabel()}</span>
      <span className={prefix('count')}>{count}</span>
    </div>
  );
};
